import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Plus, X, PlusCircle, Sparkles, Share2 } from 'lucide-react';
import SharePlanbloxModal from './SharePlanbloxModal';

const QuickActionsFAB = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isGuestMode } = useAuth();
  const [open, setOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Close FAB when navigating
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Hide for guests, unauthenticated, and on pages where it would overlap
  if (!isAuthenticated || isGuestMode) return null;
  const hiddenPaths = ['/login', '/register', '/deep-search'];
  if (hiddenPaths.some(p => location.pathname.startsWith(p))) return null;

  const actions = [
    {
      label: 'Create Event',
      icon: PlusCircle,
      color: 'bg-blue-600 hover:bg-blue-700',
      testId: 'fab-create-event',
      onClick: () => { setOpen(false); navigate('/create'); }
    },
    {
      label: 'Deep Search',
      icon: Sparkles,
      color: 'bg-emerald-600 hover:bg-emerald-700',
      testId: 'fab-deep-search',
      onClick: () => { setOpen(false); navigate('/deep-search'); }
    },
    {
      label: 'Share PlanBlox',
      icon: Share2,
      color: 'bg-violet-600 hover:bg-violet-700',
      testId: 'fab-share',
      onClick: () => { setOpen(false); setShareOpen(true); }
    }
  ];

  return (
    <>
      {/* Backdrop when open */}
      {open && (
        <div
          className="fixed inset-0 z-[45] bg-black/20 lg:hidden"
          onClick={() => setOpen(false)}
          data-testid="fab-backdrop"
        />
      )}

      {/* FAB Container — mobile only, above bottom nav */}
      <div className="fixed right-4 bottom-20 z-[46] lg:hidden" data-testid="quick-actions-fab">
        {/* Expanded actions */}
        {open && (
          <div className="flex flex-col-reverse gap-3 mb-3 items-end">
            {actions.map((action, i) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.testId}
                  onClick={action.onClick}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-white text-xs font-medium shadow-lg transition-all ${action.color}`}
                  style={{ animationDelay: `${i * 50}ms` }}
                  data-testid={action.testId}
                >
                  <Icon className="h-4 w-4" />
                  {action.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Main FAB button */}
        <button
          onClick={() => setOpen(!open)}
          className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 ${
            open
              ? 'bg-slate-700 hover:bg-slate-800 rotate-45'
              : 'bg-emerald-600 hover:bg-emerald-700'
          } text-white`}
          data-testid="fab-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
        </button>
      </div>

      <SharePlanbloxModal open={shareOpen} onOpenChange={setShareOpen} />
    </>
  );
};

export default QuickActionsFAB;
